import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useState } from 'react';

export interface AutocompleteFeature {
  properties: {
    label: string;
    name: string;
    postcode: string;
    city: string;
    id: string;
    type: string;
  };
  geometry: {
    coordinates: [number, number];
  };
}

interface BanSearchProps {
  onSelectResult: (result: AutocompleteFeature) => void;
  searchQuery?: string;
  resetSearchQuery?: (query: string) => void;
  searchHouseNumberOnly?: boolean;
}

const fetchAutocompleteResults = async(query: string, searchHouseNumberOnly: boolean): Promise<AutocompleteFeature[]> => {
  try {
    const response = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5${searchHouseNumberOnly ? '&type=housenumber' : ''}`);
    const data = await response.json();
    return data.features;
  } catch (error) {
    console.error('Error fetching autocomplete results :', error);
    return [];
  }
};

const BanSearch: React.FC<BanSearchProps> = ({
  onSelectResult,
  searchQuery: externalSearchQuery,
  resetSearchQuery: resetExternalSearchQuery,
  searchHouseNumberOnly = false,
}) => {
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AutocompleteFeature[]>([]);
  const [lastSuggestion, setLastSuggestion] = useState('');

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setInternalSearchQuery(query);
  }, []);

  const updateSearchResults = useCallback(async(query: string) => {
    if (query.length > 2 && query !== lastSuggestion) {
      const results = await fetchAutocompleteResults(query, searchHouseNumberOnly);
      setSearchResults(results);
    } else if (query.length <= 2) {
      setSearchResults([]);
    }
  }, [lastSuggestion, searchHouseNumberOnly]);

  const handleSelectAddress = useCallback((result: AutocompleteFeature) => {
    const selectedAddress = result.properties.label;
    setInternalSearchQuery(selectedAddress);
    setSearchResults([]);
    setLastSuggestion(selectedAddress);
    onSelectResult(result);
  }, [onSelectResult]);

  useEffect(() => {
    if (internalSearchQuery !== '' && internalSearchQuery !== lastSuggestion) {
      const timeoutId = setTimeout(() => {
        updateSearchResults(internalSearchQuery);
      }, 300); // Add a debounce of 300ms
      return () => clearTimeout(timeoutId);
    }
  }, [internalSearchQuery, lastSuggestion, updateSearchResults]);

  // This effect handles external changes to searchQuery (e.g., "Utiliser dans la recherche")
  useEffect(() => {
    if (externalSearchQuery != undefined
      && externalSearchQuery != ''
      && resetExternalSearchQuery
      && externalSearchQuery != internalSearchQuery) {
      setInternalSearchQuery(externalSearchQuery);
      resetExternalSearchQuery('');
    }
  }, [externalSearchQuery, setInternalSearchQuery]);

  const shouldShowSuggestions = searchResults?.length > 0 && internalSearchQuery !== lastSuggestion;

  return (
    <div className="relative">
      <input
        type="text"
        value={internalSearchQuery}
        onChange={handleSearch}
        placeholder="Rechercher une adresse..."
        className="w-full rounded border border-gray-300 p-2 pr-10"
        autoComplete="nope"
      />
      <MagnifyingGlassIcon className="absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
      {shouldShowSuggestions && (
        <ul className="absolute z-[999] mt-1 w-full rounded border border-gray-300 bg-white">
          {searchResults?.map((result, index) => (
            <li
              key={index}
              onClick={() => handleSelectAddress(result)}
              className="cursor-pointer p-2 hover:bg-gray-100"
            >
              {result.properties.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BanSearch;
